import React from "react";
import { commitMutation, graphql } from "react-relay";

import { DumbNotificationProvider } from ".";
import { faCog } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import type { PushNotification } from "@capacitor/push-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import moment from "moment";

import { inCalendar, inVideocall } from "~/lib/browserExtension";
import * as device from "~/lib/device";
import { getIdentifier } from "~/lib/device";
import appHistory from "~/lib/history";
import { DebugLogger } from "~/lib/log";
import { OUTER_OBJECT_CONTEXT_CHANGE_EVENT } from "~/lib/outerObjectContext";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { isDesktopApp, isTeams, isZoom, libStore as store } from "~/lib/utils";
import { environment } from "~/models";
import toast, { ToastActionButton, type ToastId } from "~/ui/toast";

import type { sharedNotificationsMutation as notificationsMutationType } from "~/__generated__/sharedNotificationsMutation.graphql";

import styles from "./css/notifications.module.css";

const log = new DebugLogger("[Notifications]").log;
const WEB_NOTIFICATIONS_SUPPORTED = "Notification" in window;

export enum DeviceContext {
	IOS = "ios",
	ANDROID = "android",
	DESKTOP = "desktop",
	WEB = "web",
}

export function getDeviceContext(): DeviceContext | null {
	let result: DeviceContext | null = null;
	const platform = window.Capacitor.platform;

	if (platform === "ios") {
		result = DeviceContext.IOS;
	} else if (platform === "android") {
		result = DeviceContext.ANDROID;
	} else if (isDesktopApp) {
		// Electron is handled in desktop code
	} else if (isTeams || isZoom || inVideocall || inCalendar || window.top != window.self) {
		// Extension, Teams, anything else in an iframe have disabled notifications
	} else if (WEB_NOTIFICATIONS_SUPPORTED) {
		// Default web if notifications are supported
		result = DeviceContext.WEB;
	}

	return result;
}

export function openNotificationURL(location: string) {
	// When updating this logic make sure to keep `desktop/src/listeners.js:navigate-to listener` in sync.
	try {
		const url = new URL(location);
		if (url.hostname === window.location.hostname) {
			window.HISTORY.push(`${url.pathname}${url.search}${url.hash}`);
		} else {
			window.location.href = url.toString();
		}
	} catch (error) {
		// old behaviour
		Sentry.captureException(error);
		window.HISTORY.push(location);
	}
}

export interface WebNotificationPayload {
	data: {
		tag: string | null;
		title: string;
		subtitle?: string;
		body: string;
		url?: string;
		actor_picture_url?: string; // eslint-disable-line camelcase
	};
}

const notificationsMutation = graphql`
	mutation sharedNotificationsMutation(
		$deviceIdentifier: String!
		$devicePlatform: String!
		$notificationToken: String # optional
	) {
		userUpdateDeviceInfo(
			deviceIdentifier: $deviceIdentifier
			devicePlatform: $devicePlatform
			notificationToken: $notificationToken
		) {
			user {
				id
			}
		}
	}
`;

export function commitNewToken(id: string, platform: string, token: string | null) {
	commitMutation<notificationsMutationType>(environment, {
		mutation: notificationsMutation,
		variables: {
			deviceIdentifier: id,
			devicePlatform: platform,
			notificationToken: token,
		},
	});
}

// Expose this to the global namespace so wrapping apps (ie: Electron) can call it
// TODO: [NATIVEUPDATE] Remove after native app update
window.commitNewFirebaseToken = commitNewToken;

// New code to handle firebase token changes
if (window.electron && window.electron.notificationsV1) {
	window.electron.notificationsV1.registerTokenListener(async (token: string) => {
		const deviceId = await getIdentifier();

		commitNewToken(deviceId, "electron", token);
	});
}

export function getCollapseKey(notification: PushNotification | WebNotificationPayload): string | undefined {
	if (notification.data) {
		// iOS notification
		if (notification.data.aps) {
			return notification.data.aps["thread-id"];
		}

		// Web notifications
		if (notification.data.tag) {
			return notification.data.tag;
		}

		// Android notifications
		if (notification.data.collapse_key) {
			return notification.data.collapse_key;
		}
	}

	return undefined;
}

export type NotificationPermissionState = (typeof Notification)["permission"];

export abstract class NotificationsImpl {
	constructor(protected notificationsRef: DumbNotificationProvider) {
		this.notificationsRef.context.on(OUTER_OBJECT_CONTEXT_CHANGE_EVENT, id => {
			if (id !== null) {
				this.clearNotificationThread(id);
			}
		});
	}

	idIsCleared(id: string): boolean {
		return this.notificationsRef.context.isCleared(id);
	}

	get clearIds(): ReadonlySet<string> {
		return this.notificationsRef.context.outerObjectContextIds;
	}

	public abstract readonly platform: DeviceContext;

	abstract notificationPermission(): Promise<NotificationPermissionState>;
	abstract addEventListeners(): Promise<void>;
	protected abstract doRegister(deviceId: string, platform: string): Promise<void>;
	abstract clearNotificationThread(id: string): Promise<void>;
	abstract requestPermission({
		message,
		cta,
		skipToast,
	}: {
		message?: string;
		cta?: string;
		skipToast?: boolean;
	}): void;
	destroy(): void {}

	public async register(): Promise<void> {
		const { id, platform } = await device.getInfo();
		log("Registering", { id, platform });
		await this.doRegister(id, platform);
	}
}

const LAST_SHOWN_TOAST_KEY = "lastShownPermissionsToast";
let lastShownToastId: ToastId | null = null;

/* eslint-disable-next-line fellow/no-inline-disable */
/* eslint-disable react/jsx-no-bind */
export function showPermissionRequestToast({
	message,
	cta,
	onEnable,
	force = false,
}: {
	message: string;
	cta: string;
	onEnable: () => void;
	force?: boolean;
}) {
	if (!force && store.has(LAST_SHOWN_TOAST_KEY)) return;
	if (lastShownToastId && toast.isActive(lastShownToastId)) return;

	dispatchAnalyticsTrack("Notification Permission Toast Shown", { message, cta });

	function onSettingsClick(e: React.MouseEvent) {
		e.preventDefault();
		dispatchAnalyticsTrack("Notification Permission Toast Settings Clicked", { message, cta });
		appHistory.push("/settings/notifications/");
	}

	function onEnableClick(e: React.MouseEvent) {
		e.preventDefault();
		dispatchAnalyticsTrack("Notification Permission Toast Enable Clicked", { message, cta });
		onEnable();
	}

	lastShownToastId = toast.info(<p>{message}</p>, {
		autoClose: false,
		closeButton: true,
		onClose: () => {
			if (!force) store.setExpire(LAST_SHOWN_TOAST_KEY, true, moment().add(1, "week"));
			dispatchAnalyticsTrack("Notification Permission Toast Closed", { message, cta });
		},
		actions: (
			<>
				<ToastActionButton variant="secondary" onClick={onSettingsClick} className={styles.goToSettingsButton}>
					<FontAwesomeIcon icon={faCog} />
				</ToastActionButton>
				<ToastActionButton variant="secondary" onClick={onEnableClick} className="force-dark-theme">
					{cta}
				</ToastActionButton>
			</>
		),
	});
}
/* eslint-enable react/jsx-no-bind */
